import React, { useState, useEffect, useCallback, useRef, type ComponentProps } from 'react';

import { faClock } from '@fortawesome/free-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import axios from 'axios';

import { Modal } from '@shared/components/bootstrap';
import { useToggle } from '@shared/hooks/use_toggle';

import { Preview as AddressDetailPreview } from '@admin/components/address/detail/preview';
import { Preview as AddressPreview } from '@admin/components/address/preview';
import {
  DISPATCHER_LAYOUT_REFRESH_EVENT,
  DISPATCHER_ORDER_UPDATED_EVENT,
} from '@admin/components/dispatcher/dispatcher_constants';
import { DispatcherOrderRecord } from '@admin/components/dispatcher/dispatcher_types';
import { EstimatedItemList } from '@admin/components/estimated_item_list/estimated_item_list';
import { SelectWithOther } from '@admin/components/helpers/select_with_other';
import { OrderKindDescription } from '@admin/components/order_summary/order_kind_description';
import { EstimatedCUFT as OrderEstimatedCUFT } from '@admin/components/orders/estimated_cuft';
import { FormattedServiceType } from '@admin/components/orders/formatted_service_type';
import { RoomSelectionsPreview } from '@admin/components/room_selections/preview';
import { Summary as SubscriptionsSummary } from '@admin/components/subscriptions/summary';
import type { Pricing, Subscription } from '@admin/schema';
import type { Address } from '@admin/types/address';

const DURATION_OVERRIDE_REASONS = [
  'Need to adjust estimated CuFt per Hour',
  'Estimate seems off based on CuFt',
  'Estimate seems off based on Item Categories',
  'Estimate seems off based on Plan Size',
  'Estimate seems off based on Address Details',
  'This is a warehouse order',
  'This is a 3PL order',
  'There was no initial estimated duration',
];

interface EstimateData {
  estimated_duration: number;
  estimated_duration_overwritten_at: string | null;
  predicted_duration_certainty: string | null;
  estimated_duration_override_reason: string | null;
}

interface FormState {
  estimated_duration: number;
  estimated_duration_override_reason: string | null;
  estimated_duration_overwritten_at: string | null;
  estimated_duration_hour: number;
  estimated_duration_minute: number;
  estimated_duration_override: boolean;
}

/** REST order address: full Address detail fields plus preview-only keys from Rails JSON. */
type DispatcherOrderAddress = Address & {
  neighborhood?: string | null;
  businessName?: string | null;
};

type AddressLinePreview = ComponentProps<typeof AddressPreview>['address'];

const addressForLinePreview = (addr: DispatcherOrderAddress): AddressLinePreview => ({
  street: addr.street ?? null,
  aptsuite: addr.aptsuite ?? null,
  neighborhood: addr.neighborhood ?? null,
  city: addr.city ?? null,
  state: addr.state ?? null,
  zip: addr.zip ?? null,
  businessName: addr.businessName ?? null,
  business_name: addr.business_name ?? null,
});

/** Estimated line items on orders (snake_case / camelCase from API). */
interface RestOrderEstimatedItem {
  quantity: number;
  category_display_name?: string;
  categoryDisplayName?: string;
  dimensions_overwritten?: boolean;
  dimensionsOverwritten?: boolean;
  length?: number;
  width?: number;
  height?: number;
  notes?: string;
  cuft: string | number;
}

interface RestOrderRoomSelection {
  id: string;
  quantity: number;
  selected: boolean;
  room_category_id?: string;
  categoryID?: string;
}

interface RestOrderMovingOperation {
  estimated_duration?: number | null;
  estimated_items?: RestOrderEstimatedItem[];
  origin_address: DispatcherOrderAddress;
  destination_address: DispatcherOrderAddress;
}

type RestOrderAccountSubscription = Pick<Subscription, 'id' | 'name' | 'quantity'> & {
  display_quantity?: number;
  pricing: Pick<Pricing, 'amount' | 'currency'>;
};

interface RestOrderAccount {
  subscriptions?: RestOrderAccountSubscription[];
}

const restOrderFromDispatcherBoard = (order: DispatcherOrderRecord): RestOrderPayload => ({
  id: order.id,
  account_id: order.account_id ?? order.account.id,
  scheduled: order.scheduled,
  estimated_duration: order.estimated_duration,
  estimated_duration_overwritten_at: order.estimated_duration_overwritten_at ?? null,
  estimated_duration_override_reason: order.estimated_duration_override_reason ?? null,
  dispatch_offset: order.dispatch_offset,
  sla_window_size: order.sla_window_size,
  region: order.region,
  moving: order.moving,
  moving_operation: null,
  type: order.type,
  subtype: order.subtype,
  service_type: order.service_type,
  address: {
    ...order.address,
    ...(order.address.id != null ? { id: String(order.address.id) } : {}),
  } as RestOrderPayload['address'],
});

/**
 * Order payload from dispatcher board JSON merged with GET /accounts/:id/orders/:id.json.
 * Snake_case matches Rails; includes fields required by FormattedServiceType (Angular path).
 */
export interface RestOrderPayload {
  id: number;
  account_id: number;
  estimated_duration?: number | null;
  estimated_duration_overwritten_at?: string | null;
  estimated_duration_override_reason?: string | null;
  scheduled: string;
  dispatch_offset?: number | null;
  sla_window_size?: number | null;
  region?: { tz: string } | null;
  moving?: boolean;
  moving_operation?: RestOrderMovingOperation | null;
  pickup_deadline?: string | null;
  full_pack?: boolean;
  items_count?: number;
  account?: RestOrderAccount;
  full_move_out?: boolean;
  room_selections?: RestOrderRoomSelection[];
  property_square_footage?: number | string | null;
  storage?: boolean;
  estimated_items?: RestOrderEstimatedItem[];
  other?: string | null;
  address?: DispatcherOrderAddress;
  service_type?: string;
  type?: string;
  auction_pickup?: boolean;
  subtype?: string;
  maintenance?: boolean;
}

const formatDuration = (durationInSeconds: number): string => {
  if (!Number.isInteger(durationInSeconds)) return '';
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor(durationInSeconds / 60) % 60;
  const paddedMinutes = (minutes < 10 ? '0' : '') + minutes;
  return `${hours}:${paddedMinutes}`;
};

/** SLA window slot length in seconds (matches DispatcherConstants.DEFAULT_WINDOW_SLOT_DURATION). */
const SLA_WINDOW_SLOT_SECONDS = 1800;

const formatDateInTimezone = (date: string, tz: string | undefined, format: string): string => {
  const zoneOpts = tz ? { zone: tz } : {};
  const dt = DateTime.fromISO(date, zoneOpts);
  if (!dt.isValid) {
    const dtFromMillis = DateTime.fromMillis(new Date(date).getTime(), zoneOpts);
    return dtFromMillis.toFormat(format);
  }
  return dt.toFormat(format);
};

/** Parses `scheduled` to UTC ms; uses Luxon ISO parsing so offset matches board row / Angular datetz. */
const scheduledToUtcMillis = (scheduled: string): number => {
  const fromIso = DateTime.fromISO(scheduled, { setZone: true });
  if (fromIso.isValid) return fromIso.toMillis();
  return new Date(scheduled).getTime();
};

const syncDispatcherBoard = () => {
  document.dispatchEvent(new CustomEvent(DISPATCHER_LAYOUT_REFRESH_EVENT));
  document.dispatchEvent(new CustomEvent(DISPATCHER_ORDER_UPDATED_EVENT));
};

const buildFormParams = (order: RestOrderPayload, estimateData?: EstimateData): FormState => {
  const duration = estimateData?.estimated_duration ?? order.estimated_duration ?? 0;
  const overwrittenAt = estimateData?.estimated_duration_overwritten_at ?? order.estimated_duration_overwritten_at;
  const overrideReason = estimateData?.estimated_duration_override_reason ?? order.estimated_duration_override_reason;

  return {
    estimated_duration: duration,
    estimated_duration_override_reason: overrideReason ?? null,
    estimated_duration_overwritten_at: overwrittenAt ?? null,
    estimated_duration_hour: Math.floor(duration / 3600),
    estimated_duration_minute: Math.floor(duration / 60) % 60,
    estimated_duration_override: Boolean(overwrittenAt),
  };
};

interface EstimationFormContentProps {
  order: RestOrderPayload;
  boardOrder: DispatcherOrderRecord;
  mode: string;
  onClose(): void;
}

const EstimationFormContent: React.FC<EstimationFormContentProps> = ({ order, boardOrder, mode, onClose }) => {
  const [estimateData, setEstimateData] = useState<EstimateData | null>(null);
  const [form, setForm] = useState<FormState>(() => buildFormParams(order));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string>('');
  const [minStartTime, setMinStartTime] = useState<string>('');
  const [maxStartTime, setMaxStartTime] = useState<string>('');
  const [startTimeValid, setStartTimeValid] = useState(true);
  const orderRef = useRef<RestOrderPayload>(order);

  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  const tz = order.region?.tz;

  useEffect(() => {
    const scheduledMs = scheduledToUtcMillis(order.scheduled);
    const dispatchOffsetMs = (order.dispatch_offset || 0) * 1000;
    const slaWindowSize = order.sla_window_size || 0;

    const zoneOpts = tz ? { zone: tz } : {};
    const startDt = DateTime.fromMillis(scheduledMs + dispatchOffsetMs, zoneOpts);
    const minDt = DateTime.fromMillis(scheduledMs, zoneOpts);
    const maxDt = DateTime.fromMillis(scheduledMs + slaWindowSize * SLA_WINDOW_SLOT_SECONDS * 1000, zoneOpts);

    setStartTime(startDt.toFormat('HH:mm'));
    setMinStartTime(minDt.toFormat('HH:mm'));
    setMaxStartTime(maxDt.toFormat('HH:mm'));
  }, [order.scheduled, order.dispatch_offset, order.sla_window_size, tz]);

  useEffect(() => {
    let cancelled = false;
    axios.get<EstimateData>(`/orders/${order.id}/estimate.json`).then(({ data }) => {
      if (!cancelled) setEstimateData(data);
    });
    return () => {
      cancelled = true;
    };
  }, [order.id]);

  useEffect(() => {
    setForm(buildFormParams(order, estimateData ?? undefined));
  }, [order, estimateData]);

  const updateEstimatedDuration = useCallback((hour: number, minute: number) => {
    const clampedHour = Math.max(0, hour);
    const clampedMinute = Math.min(59, Math.max(0, minute));
    const durationInSeconds = clampedHour * 3600 + clampedMinute * 60;
    setForm((prev) => ({
      ...prev,
      estimated_duration_hour: clampedHour,
      estimated_duration_minute: clampedMinute,
      estimated_duration: durationInSeconds,
    }));
  }, []);

  const isStartTimeInRange = useCallback(
    (value: string) => {
      if (!value) {
        return false;
      }
      const [hours, minutes] = value.split(':').map(Number);
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return false;
      }
      const zoneOpts = tz ? { zone: tz } : {};
      const scheduledMs = scheduledToUtcMillis(order.scheduled);
      const candidate = DateTime.fromMillis(scheduledMs, zoneOpts).set({ hour: hours, minute: minutes });
      const minDt = DateTime.fromMillis(scheduledMs, zoneOpts);
      const maxDt = DateTime.fromMillis(
        scheduledMs + (order.sla_window_size || 0) * SLA_WINDOW_SLOT_SECONDS * 1000,
        zoneOpts,
      );
      return candidate >= minDt && candidate <= maxDt;
    },
    [order.scheduled, order.sla_window_size, tz],
  );

  const applyStartTimeToBoard = useCallback(
    (value: string) => {
      if (!isStartTimeInRange(value)) {
        return;
      }

      const scheduledMs = scheduledToUtcMillis(order.scheduled);
      const [hours, minutes] = value.split(':').map(Number);
      const zoneOpts = tz ? { zone: tz } : {};
      const scheduledDt = DateTime.fromMillis(scheduledMs, zoneOpts);
      const newStartDt = scheduledDt.set({ hour: hours, minute: minutes });
      const offsetSeconds = newStartDt.diff(scheduledDt, 'seconds').seconds;
      orderRef.current.dispatch_offset = offsetSeconds;
      boardOrder.dispatch_offset = offsetSeconds;
    },
    [boardOrder, isStartTimeInRange, order.scheduled, tz],
  );

  const handleStartTimeChange = useCallback(
    (value: string) => {
      setStartTime(value);
      setStartTimeValid(isStartTimeInRange(value));
    },
    [isStartTimeInRange],
  );

  const handleStartTimeBlur = useCallback(() => {
    applyStartTimeToBoard(startTime);
    syncDispatcherBoard();
  }, [applyStartTimeToBoard, startTime]);

  const handleClose = useCallback(() => {
    applyStartTimeToBoard(startTime);
    syncDispatcherBoard();
    onClose();
  }, [applyStartTimeToBoard, onClose, startTime]);

  const applyEstimateToBoard = useCallback(
    (
      params: {
        estimated_duration: number;
        estimated_duration_override: boolean;
        estimated_duration_override_reason: string | null;
      },
      estimate?: EstimateData,
    ) => {
      boardOrder.estimated_duration = params.estimated_duration;
      if (estimate?.estimated_duration_overwritten_at !== undefined) {
        boardOrder.estimated_duration_overwritten_at = estimate.estimated_duration_overwritten_at;
      }
      if (params.estimated_duration_override) {
        boardOrder.estimated_duration_override_reason = params.estimated_duration_override_reason;
      } else {
        boardOrder.estimated_duration_override_reason = null;
        boardOrder.estimated_duration_overwritten_at = null;
      }
    },
    [boardOrder],
  );

  const save = useCallback(
    async (params: {
      estimated_duration: number;
      estimated_duration_override: boolean;
      estimated_duration_override_reason: string | null;
    }) => {
      if (saving) return;
      setSaving(true);
      setSaveError(null);
      try {
        const { data } = await axios.put<EstimateData>(`/orders/${order.id}/estimate.json`, { order: params });
        setEstimateData(data);
        setForm(buildFormParams(order, data));
        applyEstimateToBoard(params, data);
        handleClose();
      } catch (e) {
        const message =
          axios.isAxiosError(e) && e.response?.data?.message
            ? String(e.response.data.message)
            : 'Failed to update estimate. Please try again.';
        setSaveError(message);
      } finally {
        setSaving(false);
      }
    },
    [applyEstimateToBoard, handleClose, order, saving],
  );

  const handleUpdate = useCallback(() => {
    save({
      estimated_duration: form.estimated_duration,
      estimated_duration_override: true,
      estimated_duration_override_reason: form.estimated_duration_override_reason,
    });
  }, [save, form.estimated_duration, form.estimated_duration_override_reason]);

  const handleReset = useCallback(() => {
    const originalDuration = estimateData?.estimated_duration ?? order.estimated_duration ?? 0;
    save({
      estimated_duration: originalDuration,
      estimated_duration_override: false,
      estimated_duration_override_reason: null,
    });
  }, [save, estimateData, order.estimated_duration]);

  const handleReasonChange = useCallback((value: string) => {
    setForm((prev) => ({ ...prev, estimated_duration_override_reason: value }));
  }, []);

  const predictedDurationCertainty = estimateData?.predicted_duration_certainty;
  const isHighCertainty = predictedDurationCertainty === 'high';
  const orderEstimatedDuration = estimateData?.estimated_duration ?? order.estimated_duration ?? 0;

  return (
    <Modal onClose={handleClose} size={Modal.Size.Large} className="dispatcher-estimation-react-modal">
      <Modal.Content>
        <div className="dispatcher-estimation-modal-close-wrap">
          <Modal.Close close={handleClose} disabled={saving} />
        </div>
        <form name="dispatcherEstimation" className="dialog dispatcher-dialog dispatcher-estimation-form">
          <div className="dialog-contents">
            <div className="dispatcher-estimation-modal-header">
              <div className="dialog-title">Adjust Estimate</div>
            </div>
            <div className="dialog-description">
              <div className="duration-override-layout">
                <div className="duration-override-layout__label text-left">
                  <label htmlFor="estimated-duration-override-checkbox">Override Duration</label>
                </div>
                <div className="duration-override-layout__control-row duration-override">
                  <div className="duration-override__control-row-inner">
                    {isHighCertainty && (
                      <span className="duration-override__info-wrap">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          title="Estimated duration cannot be overwritten for this order"
                        />
                      </span>
                    )}
                    <input
                      id="estimated-duration-override-checkbox"
                      className="form-check-input estimated-duration-override-checkbox"
                      type="checkbox"
                      checked={form.estimated_duration_override}
                      disabled={isHighCertainty}
                      onChange={(e) => setForm((prev) => ({ ...prev, estimated_duration_override: e.target.checked }))}
                    />
                    <div className="duration-override__duration-inputs">
                      <div className="input-group">
                        <input
                          name="dispatch[estimated_duration_hour]"
                          className="form-control"
                          type="number"
                          aria-label="Estimated duration hours"
                          value={form.estimated_duration_hour}
                          onChange={(e) =>
                            updateEstimatedDuration(Number(e.target.value), form.estimated_duration_minute)
                          }
                          disabled={!form.estimated_duration_override || isHighCertainty}
                        />
                        <span className="input-group-addon">hours</span>
                      </div>
                      <div className="input-group">
                        <input
                          name="dispatch[estimated_duration_minute]"
                          className="form-control"
                          type="number"
                          aria-label="Estimated duration minutes"
                          value={form.estimated_duration_minute}
                          onChange={(e) =>
                            updateEstimatedDuration(form.estimated_duration_hour, Number(e.target.value))
                          }
                          disabled={!form.estimated_duration_override || isHighCertainty}
                        />
                        <span className="input-group-addon">minutes</span>
                      </div>
                    </div>
                    <div className="duration-override__reason-inline">
                      <SelectWithOther
                        option={form.estimated_duration_override_reason || undefined}
                        onSelect={handleReasonChange}
                        options={DURATION_OVERRIDE_REASONS}
                        disabled={!form.estimated_duration_override || isHighCertainty}
                        placeholder="Select Reason for Duration Override"
                      />
                    </div>
                  </div>
                </div>
                <div className="duration-override-layout__actions duration-override">
                  {form.estimated_duration_override && (
                    <input
                      type="submit"
                      value="Update"
                      onClick={(e) => {
                        e.preventDefault();
                        handleUpdate();
                      }}
                      className="btn btn-default btn-primary form-update-button"
                      disabled={saving || !form.estimated_duration_override_reason}
                    />
                  )}
                  {!form.estimated_duration_override && (
                    <input
                      type="submit"
                      value="Reset"
                      onClick={(e) => {
                        e.preventDefault();
                        handleReset();
                      }}
                      className="btn btn-default btn-primary form-update-button"
                      disabled={saving}
                    />
                  )}
                  {saveError && (
                    <div className="error" style={{ color: 'red' }}>
                      {saveError}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="dialog-description">
              <div className="dispatcher-dialog-notes">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <tbody>
                      <tr>
                        <th className="text-left col-nowrap col-zero">Start Time</th>
                        <td className="text-left col-fill">
                          <input
                            type="time"
                            name="startTime"
                            step={60}
                            min={minStartTime}
                            max={maxStartTime}
                            value={startTime}
                            onChange={(e) => handleStartTimeChange(e.target.value)}
                            onBlur={handleStartTimeBlur}
                            disabled={mode !== 'modifying'}
                          />
                          {!startTimeValid && (
                            <div className="error" style={{ color: 'red' }}>
                              Time must be between {minStartTime} and {maxStartTime}
                            </div>
                          )}
                        </td>
                      </tr>

                      <tr>
                        <th className="text-left col-nowrap col-zero">Estimated Job Duration</th>
                        <td className="text-left col-fill">{formatDuration(orderEstimatedDuration)}</td>
                      </tr>

                      {order.moving && order.moving_operation?.estimated_duration && (
                        <tr>
                          <th className="text-left col-nowrap col-zero">Estimated Travel Duration:</th>
                          <td className="text-left col-fill">
                            {formatDuration(order.moving_operation.estimated_duration)}
                          </td>
                        </tr>
                      )}

                      <tr>
                        <th className="text-left col-nowrap col-zero">Type:</th>
                        <td className="text-left col-fill">
                          <OrderKindDescription order={order} legacyFormat={true} />
                        </td>
                      </tr>

                      <tr>
                        <th className="text-left col-nowrap col-zero">Service Type:</th>
                        <td className="text-left col-fill">
                          <FormattedServiceType
                            order={{
                              service_type: order.service_type ?? '',
                              type: order.type ?? '',
                              auction_pickup: order.auction_pickup ?? false,
                            }}
                          />
                        </td>
                      </tr>

                      {order.pickup_deadline && (
                        <tr>
                          <th className="text-left col-nowrap col-zero">Deadline:</th>
                          <td className="text-left col-fill">
                            {formatDateInTimezone(order.pickup_deadline, tz, 'MMM d, yyyy h:mm a')}
                          </td>
                        </tr>
                      )}

                      {order.full_pack && (
                        <tr>
                          <th className="text-left col-nowrap col-zero">Full Pack:</th>
                          <td className="text-left col-fill">Yes</td>
                        </tr>
                      )}

                      <tr>
                        <th className="text-left col-nowrap col-zero">Items:</th>
                        <td className="text-left col-fill">{order.items_count}</td>
                      </tr>

                      <tr>
                        <th className="text-left col-nowrap col-zero">Estimated CuFt:</th>
                        <td className="text-left col-fill">
                          <OrderEstimatedCUFT orderID={order.id} />
                        </td>
                      </tr>

                      <tr>
                        <th className="text-left col-nowrap col-zero">Plan:</th>
                        <td className="text-left col-fill">
                          <SubscriptionsSummary subscriptions={order.account?.subscriptions ?? []} showCost={false} />
                        </td>
                      </tr>

                      {(order.full_move_out || (order.room_selections?.length ?? 0) > 0) && (
                        <tr>
                          <th className="text-left col-nowrap col-zero">Full Move-Out:</th>
                          <td className="text-left col-fill">{order.full_move_out ? 'Yes' : 'No'}</td>
                        </tr>
                      )}

                      {((order.room_selections?.length ?? 0) > 0 || order.property_square_footage) && (
                        <tr>
                          <th className="text-left col-nowrap col-zero">Rooms:</th>
                          <td className="text-left col-fill">
                            <RoomSelectionsPreview resource={order} />
                            {order.property_square_footage && (
                              <p>{Number(order.property_square_footage).toLocaleString()} sqft</p>
                            )}
                          </td>
                        </tr>
                      )}

                      {order.moving &&
                        order.moving_operation &&
                        order.moving_operation.estimated_items &&
                        order.moving_operation.estimated_items.length > 0 && (
                          <tr>
                            <th className="text-left col-nowrap col-zero">Moving Estimates:</th>
                            <td className="text-left col-fill">
                              <EstimatedItemList entries={order.moving_operation.estimated_items} />
                            </td>
                          </tr>
                        )}

                      {order.other && (
                        <tr>
                          <th className="text-left col-nowrap col-zero">Other:</th>
                          <td className="text-left col-fill">
                            <span className="preformatted">{order.other}</span>
                          </td>
                        </tr>
                      )}

                      {!order.moving_operation && order.address && (
                        <tr>
                          <th className="text-left col-nowrap col-zero">Address:</th>
                          <td className="text-left col-fill">
                            <AddressPreview address={addressForLinePreview(order.address)} />
                            <hr className="compact-hr" />
                            <AddressDetailPreview address={order.address} />
                          </td>
                        </tr>
                      )}

                      {order.moving_operation && (
                        <>
                          <tr>
                            <th className="text-left col-nowrap col-zero">Pickup Address:</th>
                            <td className="text-left col-fill">
                              <AddressPreview address={addressForLinePreview(order.moving_operation.origin_address)} />
                              <hr className="compact-hr" />
                              <AddressDetailPreview address={order.moving_operation.origin_address} />
                            </td>
                          </tr>
                          <tr>
                            <th className="text-left col-nowrap col-zero">Delivery Address:</th>
                            <td className="text-left col-fill">
                              <AddressPreview
                                address={addressForLinePreview(order.moving_operation.destination_address)}
                              />
                              <hr className="compact-hr" />
                              <AddressDetailPreview address={order.moving_operation.destination_address} />
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal.Content>
    </Modal>
  );
};

export const DispatcherEstimationFormModal: React.FC<{
  order: RestOrderPayload;
  boardOrder: DispatcherOrderRecord;
  mode: string;
}> = ({ order, boardOrder, mode }) => {
  const { visible, onToggle } = useToggle();
  const [freshOrder, setFreshOrder] = useState<RestOrderPayload>(order);

  const handleOpen = useCallback(() => {
    const boardSnapshot = boardOrder;
    setFreshOrder(restOrderFromDispatcherBoard(boardSnapshot));
    onToggle();
    const accountId = boardSnapshot.account_id ?? boardSnapshot.account.id;
    axios
      .get<Partial<RestOrderPayload>>(`/accounts/${accountId}/orders/${boardSnapshot.id}.json`)
      .then(({ data }) => {
        const merged: RestOrderPayload = {
          ...restOrderFromDispatcherBoard(boardSnapshot),
          ...data,
          scheduled: boardSnapshot.scheduled,
          dispatch_offset: boardSnapshot.dispatch_offset ?? 0,
          region: boardSnapshot.region ?? data.region ?? null,
        };
        setFreshOrder(merged);
      })
      .catch(() => {
        /* Keep board snapshot already shown */
      });
  }, [boardOrder, onToggle]);

  return (
    <>
      <a
        className="dispatcher-link"
        href=""
        onClick={(e) => {
          e.preventDefault();
          handleOpen();
        }}
      >
        <FontAwesomeIcon icon={faClock} />
        {order.estimated_duration ? (
          <span>
            {order.moving && order.moving_operation?.estimated_duration ? (
              <span>{formatDuration(order.estimated_duration + order.moving_operation.estimated_duration)}</span>
            ) : (
              <span>{formatDuration(order.estimated_duration)}</span>
            )}
          </span>
        ) : (
          <span> Add</span>
        )}
      </a>
      {visible && <EstimationFormContent order={freshOrder} boardOrder={boardOrder} mode={mode} onClose={onToggle} />}
    </>
  );
};
